import { createSlice } from '@reduxjs/toolkit';
import { clearStorage, fetchFromStorage, removeFromStorage, saveToStorage } from '../../storage';
import { identifiers } from '../../constants/identifiers';
import { URLS } from '../../constants/urls';
import axiosInstance from '../axios';
import strings from '../../../common/Translation/Translate';

const fromStorage = fetchFromStorage(identifiers.access_token) as any;
const link = fetchFromStorage(identifiers.preview);

interface AuthenticationState {
  user: any;
  authenticated: boolean;
  privilege: any;
  translation: any;
  priviewLink: string | null;
  languageList: [] | null;
  selectedlanguage: any;
  guestUser: any;
  orgInfo: null;
  theme: {
    primary: string;
    primaryTextColor: string;
    secondaryMain: string;
    secondaryDark: string;
    secondaryLight: string;
    secondaryTextColor: string;
    hoverColor: string;
    buttonGradiant?: string;
    headerColor?: string;
  };
  openAuthenticationDrawer: boolean;
  initialData: any;
  gaInitialized: boolean;
}

const authenticationSlice = createSlice({
  name: 'authenticationSlice',
  initialState: {
    orgInfo: {},
    authenticated: fromStorage ? true : false,
    theme: fetchFromStorage(identifiers.theme)
      ? JSON.parse(fetchFromStorage(identifiers.theme) as any)
      : {
          primary: '#9A2143',
          primaryTextColor: '#FFFFFF',
          secondaryMain: '#BFA054',
          secondaryDark: 'FFF5DB',
          secondaryLight: '#FBF8F2',
          secondaryTextColor: '#000000',
          hoverColor: '#79112E',
        },
    user: fetchFromStorage(identifiers.userDetails)
      ? JSON.parse(fetchFromStorage(identifiers.userDetails) as string)
      : null,
    priviewLink: link || '',
    selectedlanguage: fetchFromStorage(identifiers.selectedLanguage)
      ? JSON.parse(fetchFromStorage(identifiers.selectedLanguage) as any)
      : { value: '', text: '' },
    privilege: fetchFromStorage(identifiers.privilegeList),
    translation: fetchFromStorage(identifiers.translation),
    languageList: fetchFromStorage(identifiers.languages) && JSON.parse(fetchFromStorage(identifiers.languages) as any),
    initialData: fetchFromStorage(identifiers.initialData)
      ? JSON.parse(fetchFromStorage(identifiers.initialData) as any)
      : { forcedLogin: false },
    openAuthenticationDrawer: false,
    gaInitialized: false,
    guestUser: null,
  },
  reducers: {
    /**
     * Stores token material (and optional tenant/org hints from the token endpoint).
     * Call {@link fetchUserContext} after login or refresh to hydrate user, privileges, preview link.
     */
    setAuthTokens(state, action) {
      if (!action.payload) {
        state.authenticated = false;
        removeFromStorage(identifiers.access_token);
        return;
      }
      const p = action.payload as any;
      const accessToken = p.accessToken ?? p.access_token;
      const refreshTokenVal = p.refreshToken ?? p.refresh_token;
      if (!accessToken) {
        return;
      }
      state.authenticated = true;
      saveToStorage(identifiers.access_token, accessToken);
      if (refreshTokenVal) {
        saveToStorage(identifiers.refresh_token, refreshTokenVal);
      }
      const tenantId = p.tenantId;
      if (tenantId !== undefined && tenantId !== null) {
        saveToStorage('tenantId', tenantId);
      }
    },
    /**
     * Applies {@code GET /authenticated/user-context} payload to the store and storage.
     */
    setUserContext(state, action) {
      if (!action.payload) return;
      const p = action.payload as any;
      const { email, username, privileges, previewLink, organization, version, tenantId } = p;

      if (previewLink !== undefined) {
        saveToStorage(identifiers.preview, previewLink);
        state.priviewLink = previewLink;
      }
      if (privileges !== undefined) {
        saveToStorage(identifiers.privilegeList, privileges);
        state.privilege = privileges;
      }
      if (username !== undefined || email !== undefined) {
        const userDetails = JSON.stringify({ username: username ?? email, email: email ?? username });
        saveToStorage(identifiers.userDetails, userDetails);
        state.user = { username: username ?? email, email: email ?? username };
      }
      if (organization !== undefined && organization !== null) {
        state.orgInfo = organization as any;
      }
      if (version !== undefined) saveToStorage('version', version);
      if (tenantId !== undefined) saveToStorage('tenantId', tenantId);
    },
    /**
     * Back-compat: slim token-only payloads use {@link setAuthTokens}; payloads that already include
     * privileges / preview / profile fields also run {@link setUserContext}.
     */
    setAuthentication(state, action) {
      if (!action.payload) {
        authenticationSlice.caseReducers.setAuthTokens(state, { ...action, payload: null } as any);
        return;
      }
      authenticationSlice.caseReducers.setAuthTokens(state, action as any);
      const p = action.payload as any;
      if ('privileges' in p || 'previewLink' in p) {
        authenticationSlice.caseReducers.setUserContext(state, action as any);
      }
    },
    logout(state) {
      clearStorage('local');
      state.authenticated = false;
    },
    setUser(state, action) {
      state.user = action.payload;
    },
    setOrgInfo(state, action) {
      state.orgInfo = action.payload;
    },
    setTranslation(state, action) {
      state.translation = action.payload;
    },
    setLanguageList(state, action) {
      saveToStorage(identifiers.languages, JSON.stringify(action.payload));
      state.languageList = action.payload;
    },
    setTheme(state, action) {
      console.log('#err-theme-action-payload: ', action.payload);
      saveToStorage(identifiers.theme, JSON.stringify(action.payload));
      state.theme = action.payload;
    },
    setSelectedLanguage(state, action) {
      saveToStorage(identifiers.selectedLanguage, JSON.stringify(action.payload));
      state.selectedlanguage = action.payload;
    },
    setIniatalData(state, action) {
      saveToStorage(identifiers.initialData, JSON.stringify(action.payload));
      state.initialData = action.payload;
    },
    setOpenAuthenticationDrawer(state, action) {
      state.openAuthenticationDrawer = action.payload;
    },
    setGaInitialized(state, action) {
      state.gaInitialized = action.payload;
    },
    setGuestUser(state, action) {
      state.guestUser = action.payload;
    },
  },
});

export const refreshToken = (refreshtoken?: string) => async (dispatch: any) => {
  const token = fetchFromStorage(identifiers.refresh_token);

  try {
    const { status, data } = await axiosInstance.post(URLS.keycloakRefreshToken, {
      refreshToken: refreshtoken || token || '',
    });
    if (status === 200) {
      dispatch(authenticationSlice.actions.setAuthTokens(data));
      await dispatch(fetchUserContext() as any);
    }
  } catch (e) {
    return e;
  }
};

/**
 * Loads profile, privileges, and related flags from {@code GET /authenticated/user-context}.
 */
export const fetchUserContext = (onSuccess?: () => void) => async (dispatch: any) => {
  try {
    const { status, data } = await axiosInstance.get(URLS.userContext);
    if (status === 200) {
      dispatch(authenticationSlice.actions.setUserContext(data));
      onSuccess && onSuccess();
      return data;
    }
  } catch (error: any) {
    console.error('Failed to fetch user context', error);
    throw error;
  }
};

export const handleLanguageChange = (lang: any) => async (dispatch: any) => {
  try {
    const { status, data } = await axiosInstance.get(URLS.getCustomerTranslations(lang.value));
    if (status === 200) {
      dispatch(setSelectedLanguage(lang));
      dispatch(setTranslation(data));
      strings.setContent({
        [lang]: data,
      });
    }
  } catch (e) {
    return e;
  }
};

export const handleLanguageOnChange = (lang: any) => async (dispatch: any) => {
  dispatch(handleLanguageChange(lang) as any);
  window.location.reload();
};

export default authenticationSlice.reducer;

export const {
  setAuthentication,
  setAuthTokens,
  setUserContext,
  setOrgInfo,
  logout,
  setUser,
  setTranslation,
  setLanguageList,
  setTheme,
  setSelectedLanguage,
  setIniatalData,
  setOpenAuthenticationDrawer,
  setGaInitialized,
  setGuestUser,
} = authenticationSlice.actions;

export const User = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.user;
export const getTheme = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.theme;
export const Privilege = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.privilege;
export const translation = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.translation;
export const languages = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.languageList;
export const organizationInfo = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.orgInfo;
export const defaultLanguage = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.selectedlanguage;
export const Authenticated = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.authenticated;
export const priviewLink = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.priviewLink;
export const openAuthenticationDrawer = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.openAuthenticationDrawer;
export const GaInitialized = (state: { authenticationSlice: AuthenticationState }) =>
  state.authenticationSlice.gaInitialized;

export const initData = (state: { authenticationSlice: AuthenticationState }) => state.authenticationSlice.initialData;
